import ApplicationVersion from "@/utils/constants/ApplicationVersion";
import ClientCache from "@/utils/login/ClientCache.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import Translator from "@/utils/common/Translator.ts";
import {Ref, ref} from "vue";

export default class DefaultApplicationInfo {
  static remainingSupportDays: Ref<number | null> = ref(null);
  static supportEndDate: Ref<string | null> = ref(null);
  static customerSupportAlertPeriod: Ref<number | null> = ref(null);

  static applicationVersion() {
    return ApplicationVersion.SERVER_VERSION + "." + ApplicationVersion.DATABASE_VERSION + "."
      + this.lpad(ApplicationVersion.BUILD_YEAR % 100, ApplicationVersion.BUILD_MONTH, ApplicationVersion.BUILD_DAY) + "."
      + this.lpad(ApplicationVersion.BUILD_HOUR, ApplicationVersion.BUILD_MINUTE);
  }

  static getBuildDate() {
    let buildDate = ApplicationVersion.BUILD_YEAR + "-" + this.lpad(ApplicationVersion.BUILD_MONTH) + "-" + this.lpad(ApplicationVersion.BUILD_DAY) + " " + this.lpad(
      ApplicationVersion.BUILD_HOUR) + ":" + this.lpad(ApplicationVersion.BUILD_MINUTE);
    if (ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.subRelease)
      buildDate += buildDate + "-" + ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.subRelease;
    return buildDate;
  }

  static async getLatestRelease() {
    let latestRelease = await NamaCommon.namaFetch("https://nlm.namasoft.com/nlm/download.php?releasename=true&subr=" + (ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.subRelease ?? ""), {method: "GET"});
    if (!NamaCommon.isSuccess(latestRelease))
      return 'Could not connect';
    latestRelease = latestRelease.toString()
    return latestRelease.substring(0, 4) + "-" + latestRelease.substring(4, 6) + "-" + latestRelease.substring(6, 8) + " " + latestRelease
      .substring(8, 10) + ":" + latestRelease.substring(10);

  }

  static appVersion() {
    return ApplicationVersion.SERVER_VERSION + "." + ApplicationVersion.DATABASE_VERSION;
  }

  static async getCustomerSupportEndDate(): Promise<string> {
    let customerName = this.calcCustomerName();
    if (!customerName)
      return "Could not connect";
    let data = await NamaCommon.namaFetch("https://namasoft.com/nlm/cmods?fetchexpirydate=" + customerName, {method: "GET"});
    if (!NamaCommon.isSuccess(data))
      return 'Could not connect'
    return data
  }

  static calcCustomerName(): string {
    return ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.customerName ?? (window as any).customer;
  }

  static async getLicenceDetails() {
    let customerName = this.calcCustomerName();
    if (!customerName)
      return "Could not connect";
    let ss = ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.subServer;
    ss = ss ?? "";
    let data = await NamaCommon.namaFetch("https://namasoft.com/nlm/cmods?fetchlecount=" + customerName + "&ss=" + ss, {method: "GET"});
    if (!NamaCommon.isSuccess(data))
      return 'Could not connect'
    return data;
  }

  private static lpad(...parts: any[]) {
    let txt = "";
    for (let part of parts) {
      if (part > 1999)
        part = part - 2000;
      if (part < 10)
        txt += "0" + part;
      else
        txt += part;
    }
    return txt;
  }

  static async getCustomerSupportAlertPeriod(): Promise<number> {
    let customerName = ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.customerName;
    customerName = customerName ?? "";
    let value = await NamaCommon.namaFetch("https://namasoft.com/nlm/cmods?fetchalertperiod=" + customerName, {method: "GET"});
    return value || 25;
  }

  static async updateSupportDateAlerts(): Promise<any> {
    if (!LoginUtils.loginInfo?.userInfo)
      return;
    let userInfo = LoginUtils.loginInfo.userInfo;
    if (userInfo.settings && userInfo.settings?.doNotDisplayCriticalErrors === true)
      return;
    if (userInfo.fullProfile && userInfo.fullProfile.doNotDisplayCriticalErrors === true)
      return;
    if (this.remainingSupportDays.value === null) {
      this.supportEndDate.value = await DefaultApplicationInfo.getCustomerSupportEndDate();
      if (!this.supportEndDate.value)
        return;
      const parsedDate = new Date(this.supportEndDate.value).getTime();
      if (isNaN(parsedDate))
        return;
      this.remainingSupportDays.value = (parsedDate - new Date().getTime()) / (24 * 60 * 60 * 1000);
      this.customerSupportAlertPeriod.value = await DefaultApplicationInfo.getCustomerSupportAlertPeriod();
    }
    if (!this.customerSupportAlertPeriod.value || this.customerSupportAlertPeriod.value <= 0)
      return;
    return;
  }

  static getRemainingTime(): string {
    if (!this.remainingSupportDays.value)
      return '';
    const days = Math.abs(Math.round(this.remainingSupportDays.value));
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    const remainingDays = days % 30;
    if (Translator.isArabic()) {
      const parts: string[] = [];
      if (years)
        parts.push(`${years} سنة`);
      if (months)
        parts.push(`${months} شهر`);
      if (remainingDays || !parts.length)
        parts.push(`${remainingDays} يوم`);
      return this.remainingSupportDays.value < 0 ? `منذ ${parts.join(" و ")}` : `متبقي ${parts.join(" و ")}`;
    }
    const parts: string[] = [];
    if (years)
      parts.push(`${years} year${years > 1 ? "s" : ""}`);
    if (months)
      parts.push(`${months} month${months > 1 ? "s" : ""}`);
    if (remainingDays || !parts.length)
      parts.push(`${remainingDays} day${remainingDays > 1 ? "s" : ""}`);
    return this.remainingSupportDays.value < 0 ? `${parts.join(", ")} ago` : `${parts.join(", ")} remaining`;
  }
}
